import React, { useEffect, useState, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import useApi from "../hook/useApi";
import Loader from "../components/Loader/Loader";
import Swal from "sweetalert2";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import "./MasterLayout.css";
import { useBranding } from "../context/BrandingContext";
import { formatCurrency } from "../utils/helper";

const MasterLayout = ({ children }) => {
  const { profile, setProfile, logout: logoutAdmin, subAdminId } = useAuth();
  const { AsyncGetApiCall, AsyncPatchApiCall } = useApi();
  const { branding: dynamicBranding } = useBranding();
  const [branding, setBranding] = useState({
    appName: "ProTrade",
    copyrightText: "\u00a9 ProTrade. All Rights Reserved.",
  });

  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation();

  const [subadminProfileOpen, setSubadminProfileOpen] = useState(false);
  const [subadminProfileLoading, setSubadminProfileLoading] = useState(false);
  const profileDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        // Find existing open dropdowns and close them
        const dropdown =
          profileDropdownRef.current.querySelector(".dropdown-menu");
        if (dropdown && dropdown.classList.contains("show")) {
          const toggle = profileDropdownRef.current.querySelector(
            '[data-bs-toggle="dropdown"]',
          );
          if (toggle) toggle.click(); // Simple way to trigger Bootstrap's toggle off
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [subadminForm, setSubadminForm] = useState({
    loginId: "",
    password: "",
    masterName: "",
    remarks: "",
    partnership: "",
    onlyPosition: false,
    limitOrders: true,
  });

  const openSubadminProfileModal = () => {
    if (profile && subAdminId) {
      setSubadminForm({
        loginId: profile.loginId || "",
        password: "",
        masterName: profile.masterName || "",
        remarks: profile.remarks ?? "",
        partnership:
          profile.partnership != null && profile.partnership !== ""
            ? String(profile.partnership)
            : "",
        onlyPosition: Boolean(profile.onlyPosition),
        limitOrders:
          profile.limitOrders !== undefined
            ? Boolean(profile.limitOrders)
            : true,
      });
    }
    setSubadminProfileOpen(true);
  };

  const handleSubadminFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSubadminForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubadminProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profile?._id) {
      toast.error("Profile not loaded");
      return;
    }
    const partnershipNum = Number(subadminForm.partnership);
    if (
      subadminForm.partnership === "" ||
      Number.isNaN(partnershipNum) ||
      partnershipNum < 0
    ) {
      toast.error("Enter a valid partnership value (0 or greater)");
      return;
    }

    setSubadminProfileLoading(true);
    try {
      const payload = {
        id: profile._id,
        loginId: subadminForm.loginId.trim(),
        masterName: subadminForm.masterName.trim(),
        remarks: subadminForm.remarks.trim(),
        partnership: partnershipNum,
        onlyPosition: subadminForm.onlyPosition,
        limitOrders: subadminForm.limitOrders,
      };
      if (subadminForm.password?.trim()) {
        payload.password = subadminForm.password.trim();
      }

      const result = await AsyncPatchApiCall("/subadmin/update", payload);

      if (result?.errors) {
        toast.error(
          Array.isArray(result.errors) ? result.errors[0] : result.errors,
        );
      } else if (result?.success) {
        const next = result.data || {
          ...profile,
          loginId: payload.loginId,
          masterName: payload.masterName,
          remarks: payload.remarks,
          partnership: payload.partnership,
          onlyPosition: payload.onlyPosition,
          limitOrders: payload.limitOrders,
        };
        setProfile(next);
        sessionStorage.setItem("profile", JSON.stringify(next));
        toast.success(result.message || "Profile updated");
        setSubadminProfileOpen(false);
        setSubadminForm((prev) => ({ ...prev, password: "" }));
      } else {
        toast.error(result?.message || "Update failed");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSubadminProfileLoading(false);
    }
  };

  useEffect(() => {
    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      // Close all dropdowns
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = "0px"; // Collapse submenu
        }
      });

      // Toggle the clicked dropdown
      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = `${submenu.scrollHeight}px`; // Expand submenu
        }
      }
    };

    // Attach click event listeners to all dropdown triggers
    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link, .sidebar-menu .dropdown > button",
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          if (
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname
          ) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) {
              submenu.style.maxHeight = `${submenu.scrollHeight}px`; // Expand submenu
            }
          }
        });
      });
    };

    // Open the submenu that contains the active route
    openActiveDropdown();

    // Cleanup event listeners on unmount
    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname]);

  useEffect(() => {
    if (subadminProfileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [subadminProfileOpen]);

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  // Fetch branding once on mount
  useEffect(() => {
    AsyncGetApiCall("/system-config")
      .then((res) => {
        if (res?.success && res?.data?.branding) {
          setBranding(res.data.branding);
        }
      })
      .catch(() => { });
  }, [AsyncGetApiCall]);

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      {subadminProfileLoading && <Loader />}
      {/* sidebar */}
      <aside
        className={`${sidebarActive ? "sidebar active" : mobileMenu ? "sidebar sidebar-open" : "sidebar"}`}
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link to="/dashboard" className="sidebar-logo d-flex align-items-center gap-2" style={{ textDecoration: 'none' }}>
            <img
              src={dynamicBranding?.whiteLogoUrl || "/assets/images/logo-white.svg"}
              alt={dynamicBranding?.appName || "site logo"}
              className="white-logo"
              style={{ maxHeight: "40px", maxWidth: "40px", objectFit: "contain", flexShrink: 0 }}
            />
            <span style={{ color: "white", fontWeight: 600, fontSize: "16px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {dynamicBranding?.appName || branding?.appName || "ProTrade"}
            </span>
          </Link>
        </div>
        <div className="sidebar-menu-area text-white">
          <ul className="sidebar-menu" id="sidebar-menu">
            <li>
              <NavLink
                to="/dashboard"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon
                  icon="solar:home-smile-angle-outline"
                  className="menu-icon"
                />
                <span>Dashboard</span>
              </NavLink>
            </li>

            {profile?.role === "SUPERADMIN" && (
              <li>
                <NavLink
                  to="/white-labels"
                  className={(navData) => (navData.isActive ? "active-page" : "")}
                >
                  <Icon
                    icon="solar:globus-outline"
                    className="menu-icon"
                  />
                  <span>White Labels</span>
                </NavLink>
              </li>
            )}

            <>
              <li>
                <NavLink
                  to="/trading/watchlist"
                  className={(navData) => (navData.isActive ? "active-page" : "")}
                >
                  <Icon icon="tabler:list-details" className="menu-icon" />
                  <span>Watchlist</span>
                </NavLink>
              </li>

              {!subAdminId && (
                <li>
                  <NavLink
                    to="/subadmin-list"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon
                      icon="mdi:shield-account-outline"
                      className="menu-icon"
                    />
                    <span>Subadmin Management</span>
                  </NavLink>
                </li>
              )}
              <li>
                <NavLink
                  to="/users-list"
                  className={(navData) => (navData.isActive ? "active-page" : "")}
                >
                  <Icon
                    icon="flowbite:users-group-outline"
                    className="menu-icon"
                  />
                  <span>User Management</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/broker-list"
                  className={(navData) => (navData.isActive ? "active-page" : "")}
                >
                  <Icon icon="solar:shield-user-outline" className="menu-icon" />
                  <span>Broker Management</span>
                </NavLink>
              </li>

              <li className="dropdown">
                <Link to="#">
                  <Icon
                    icon="tabler:adjustments-horizontal"
                    className="menu-icon"
                  />
                  <span>Trading Management</span>
                </Link>
                <ul className="sidebar-submenu overflow-y-auto">
                  <li>
                    <NavLink
                      to="/trading/trades"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:hierarchy-2" className="menu-icon" />
                      <span>Trades</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/trading/pending-trades"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:history" className="menu-icon" />
                      <span>Pending Trades</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/trading/portfolio"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:file-text" className="menu-icon" />
                      <span>Portfolio / Position</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/trading/margin-management"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:chart-pie" className="menu-icon" />
                      <span>Margin Management</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/trading/summary-report"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon
                        icon="tabler:report-analytics"
                        className="menu-icon"
                      />
                      <span>Summary Report</span>
                    </NavLink>
                  </li>
                </ul>
              </li>

              {!subAdminId && (
                <li>
                  <NavLink
                    to="/tv-channel-list"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon="tabler:device-tv" className="menu-icon" />
                    <span>TV Channel Management</span>
                  </NavLink>
                </li>
              )}
              {!subAdminId && (
                <li>
                  <NavLink
                    to="/cms-management"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon="tabler:file-text" className="menu-icon" />
                    <span>CMS Management</span>
                  </NavLink>
                </li>
              )}
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="tabler:user-circle" className="menu-icon" />
                  <span>Accounts Management</span>
                </Link>
                <ul className="sidebar-submenu overflow-y-auto">
                  <li>
                    <NavLink
                      to="/accounts/ledger"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:receipt" className="menu-icon" />
                      <span>Ledger</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/accounts/cash-ledger"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:wallet" className="menu-icon" />
                      <span>Cash Ledger</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/accounts/cash-entry"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:circle-plus" className="menu-icon" />
                      <span>Cash Entry</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/accounts/jv"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:plus" className="menu-icon" />
                      <span>JV</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/accounts/trial-balance"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:receipt" className="menu-icon" />
                      <span>Trial Balance</span>
                    </NavLink>
                  </li>
                </ul>
              </li>

              <li className="dropdown">
                <Link to="#">
                  <Icon icon="tabler:database" className="menu-icon" />
                  <span>Utilities / Audit Logs</span>
                </Link>
                <ul className="sidebar-submenu overflow-y-auto">
                  <li>
                    <NavLink
                      to="/utilities/trade-edit-delete-log"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:list" className="menu-icon" />
                      <span>Trade Edit/Delete Log</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/utilities/user-edit-log"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:list" className="menu-icon" />
                      <span>User Edit Log</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/utilities/cash-edit-delete-log"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:list" className="menu-icon" />
                      <span>Cash Edit/Delete Log</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/utilities/auto-squareup-log"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:list" className="menu-icon" />
                      <span>Auto SquareUp Log</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/utilities/rejection-log"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:list" className="menu-icon" />
                      <span>Rejection Log</span>
                    </NavLink>
                  </li>
                </ul>
              </li>

              {!subAdminId && (
                <li className="dropdown">
                  <Link to="#">
                    <Icon
                      icon="tabler:settings-automation"
                      className="menu-icon"
                    />
                    <span>Trading Rules</span>
                  </Link>
                  <ul className="sidebar-submenu overflow-y-auto">
                    <li>
                      <NavLink
                        to="/trading-settings/segments"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                      >
                        <Icon icon="tabler:category" className="menu-icon" />
                        <span>Segments</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/trading-settings/scripts-list"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                      >
                        <Icon icon="tabler:list" className="menu-icon" />
                        <span>Scripts list</span>
                      </NavLink>
                    </li>
                  </ul>
                </li>
              )}

              {!subAdminId && (
                <>
                  <li className="dropdown">
                    <Link to="#">
                      <Icon icon="solar:settings-outline" className="menu-icon" />
                      <span>Quantity Setting</span>
                    </Link>
                    <ul className="sidebar-submenu overflow-y-auto">
                      <li>
                        <NavLink
                          to="/settings/account-type"
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <Icon icon="tabler:category-2" className="menu-icon" />
                          <span>Account Type</span>
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/settings/create-qty"
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <Icon icon="tabler:file-plus" className="menu-icon" />
                          <span>Create Qty File</span>
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/settings/manage-qty"
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <Icon icon="tabler:file-settings" className="menu-icon" />
                          <span>Manage Qty File</span>
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/settings/list-qty"
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <Icon
                            icon="tabler:clipboard-list"
                            className="menu-icon"
                          />
                          <span>List Qty File</span>
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/settings/block-scripts"
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <Icon icon="tabler:lock" className="menu-icon" />
                          <span>Block/Allow scripts</span>
                        </NavLink>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <NavLink
                      to="/accounts/settlement-management"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon="tabler:arrows-exchange" className="menu-icon" />
                      <span>Settlement Management</span>
                    </NavLink>
                  </li>
                </>
              )}

              <li>
                <NavLink
                  to="/wallet-funds"
                  className={(navData) => (navData.isActive ? "active-page" : "")}
                >
                  <Icon icon="tabler:wallet" className="menu-icon" />
                  <span>Wallet & Funds</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/commission-revenue"
                  className={(navData) => (navData.isActive ? "active-page" : "")}
                >
                  <Icon icon="tabler:coins" className="menu-icon" />
                  <span>Commission & Revenue</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/reports-analytics"
                  className={(navData) => (navData.isActive ? "active-page" : "")}
                >
                  <Icon icon="tabler:chart-bar" className="menu-icon" />
                  <span>Reports & Analytics</span>
                </NavLink>
              </li>
              {!subAdminId && (
                <li>
                  <NavLink
                    to="/system-config"
                    className={(navData) => (navData.isActive ? "active-page" : "")}
                  >
                    <Icon icon="tabler:settings" className="menu-icon" />
                    <span>System Config</span>
                  </NavLink>
                </li>
              )}
            </>
            {/* <li>
              <NavLink
                to="/notifications"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon="tabler:bell" className="menu-icon" />
                <span>Notifications</span>
              </NavLink>
            </li> */}
            {/* <li>
              <NavLink
                to="/audit-logs"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon="tabler:file-search" className="menu-icon" />
                <span>Audit Logs</span>
              </NavLink>
            </li> */}
            {/* <li>
              <NavLink
                to="/api-control"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon="tabler:api" className="menu-icon" />
                <span>API Control</span>
              </NavLink>
            </li> */}
          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon="iconoir:arrow-right"
                      className="icon text-2xl non-active"
                    />
                  ) : (
                    <Icon
                      icon="heroicons:bars-3-solid"
                      className="icon text-2xl non-active "
                    />
                  )}
                </button>
                <button
                  type="button"
                  className={`panel-badge d-inline-flex align-items-center gap-8 px-16 py-10 radius-8 fw-semibold text-sm border-0 shadow-sm ${subAdminId
                    ? "bg-primary-100 text-primary-700"
                    : "bg-warning-focus text-warning-700"
                    }`}
                  style={{ cursor: "default" }}
                  title={
                    subAdminId
                      ? "You are logged in to the Sub-Admin panel"
                      : profile?.role === "SUPERADMIN"
                        ? "You are logged in to the Super-Admin panel"
                        : "You are logged in to the Admin panel"
                  }
                  aria-label={
                    subAdminId
                      ? "Panel: Sub-Admin"
                      : profile?.role === "SUPERADMIN"
                        ? "Panel: Super-Admin"
                        : "Panel: Admin"
                  }
                >
                  <Icon
                    icon={
                      subAdminId
                        ? "mdi:account-supervisor-outline"
                        : profile?.role === "SUPERADMIN"
                          ? "mdi:shield-crown-outline"
                          : "mdi:shield-account-outline"
                    }
                    className="icon text-xl flex-shrink-0"
                  />
                  <span>
                    {subAdminId
                      ? "Sub-Admin Panel"
                      : profile?.role === "SUPERADMIN"
                        ? "Super-Admin Panel"
                        : "Admin Panel"}
                  </span>
                </button>
                <button
                  onClick={mobileMenuControl}
                  type="button"
                  className="sidebar-mobile-toggle"
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon" />
                </button>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                {/* Static wallet widget removed because it does not fetch live data on navigation */}
                <ThemeToggleButton />
                <div className="dropdown" ref={profileDropdownRef}>
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle"
                    type="button"
                    data-bs-toggle="dropdown"
                    onClick={async () => {
                      try {
                        const endpoint = subAdminId ? `/subadmin/get-subadmin/${profile?._id}` : "/admin/profile";
                        const res = await AsyncGetApiCall(endpoint);
                        if (res?.success && res.data) {
                          setProfile(res.data);
                          sessionStorage.setItem("profile", JSON.stringify(res.data));
                        }
                      } catch (e) {
                        console.error("Something went wrong", e)
                      }
                    }}
                  >
                    <img
                      src="https://wow-dash.com/demo/assets/images/user.png"
                      alt="image_user"
                      className="w-40-px h-40-px object-fit-cover rounded-circle"
                    />
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm">
                    <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex flex-column gap-2">
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <div>
                          <h6 className="text-lg text-primary-light fw-semibold mb-0">
                            {profile?.masterName || profile?.name || "User"}
                          </h6>
                        </div>
                        <button type="button" className="hover-text-danger">
                          <Icon
                            icon="radix-icons:cross-1"
                            className="icon text-xl"
                          />
                        </button>
                      </div>
                      <div className="d-flex flex-column bg-white p-4 radius-8 border border-primary-100 shadow-sm mt-2">
                        <div className="d-flex justify-content-between align-items-center px-2">
                          <span className="text-xs text-secondary-light">Real Balance</span>
                          <span className="text-primary-600 fw-bold">{formatCurrency(profile?.currentBalance)}</span>
                        </div>
                        {(profile?.unsettledPnl !== 0 && profile?.unsettledPnl !== undefined) && (
                          <div className="d-flex justify-content-between align-items-center mt-1 pt-1 border-top border-primary-100">
                            <span className="text-xs text-secondary-light" title="Running PnL + Upfront Brokerage">Unsettled P&L</span>
                            <span className={`fw-bold ${profile?.unsettledPnl >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                              {formatCurrency(profile?.unsettledPnl)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <ul className="to-top-list">
                      {/* {subAdminId ? (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3 w-100 border-0 bg-transparent text-start"
                            onClick={openSubadminProfileModal}
                          >
                            <Icon
                              icon="solar:user-linear"
                              className="icon text-xl"
                            />{" "}
                            My Profile
                          </button>
                        </li>
                      ) : ( */}
                      <li>
                        <Link
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
                          to={subAdminId ? "/my-profile" : "/view-admin-profile"}
                        >
                          <Icon
                            icon="solar:user-linear"
                            className="icon text-xl"
                          />{" "}
                          My Profile
                        </Link>
                      </li>


                      <button
                        className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3 w-100 border-0 bg-transparent text-start"
                        onClick={async () => {
                          const { isConfirmed } = await Swal.fire({
                            title: "Sign Out?",
                            text: "Are you sure you want to log out?",
                            icon: "question",
                            showCancelButton: true,
                            confirmButtonColor: "#2352c3",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Yes, Log out!",
                            customClass: { popup: "swal-sm" },
                          });

                          if (isConfirmed) {
                            if (subAdminId) {
                              logoutAdmin(subAdminId);
                            } else {
                              logoutAdmin();
                            }
                          }
                        }}
                      >
                        <Icon icon="lucide:power" className="icon text-xl" />{" "}
                        Log Out
                      </button>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}
              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className="dashboard-main-body">{children}</div>

        {/* Footer section */}
        <footer className="d-footer">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <p className="mb-0">
                {`\u00a9 ${new Date().getFullYear()} ${dynamicBranding?.appName || branding.appName || "ProTrade"}. All Rights Reserved.`}
              </p>
            </div>
            <div className="col-auto">
              <p className="mb-0">
                {/* Made by <span className="text-primary-600">XYZ</span> */}
              </p>
            </div>
          </div>
        </footer>
      </main>

      {subadminProfileOpen && subAdminId && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content radius-16 bg-base">
              <div className="modal-header border-bottom">
                <h5 className="modal-title">My profile</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setSubadminProfileOpen(false)}
                  disabled={subadminProfileLoading}
                />
              </div>
              <form onSubmit={handleSubadminProfileSubmit}>
                <div className="modal-body">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label">
                        Login ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="loginId"
                        value={subadminForm.loginId}
                        onChange={handleSubadminFormChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        New password{" "}
                        <span className="text-secondary-light text-sm">
                          (optional)
                        </span>
                      </label>
                      <input
                        type="password"
                        className="form-control"
                        name="password"
                        value={subadminForm.password}
                        onChange={handleSubadminFormChange}
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Master name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="masterName"
                        value={subadminForm.masterName}
                        onChange={handleSubadminFormChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Partnership (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="partnership"
                        value={subadminForm.partnership}
                        onChange={handleSubadminFormChange}
                        min={0}
                        max={100}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Remarks</label>
                      <textarea
                        className="form-control"
                        name="remarks"
                        value={subadminForm.remarks}
                        onChange={handleSubadminFormChange}
                        rows={2}
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="onlyPosition"
                          id="subadmin-onlyPosition"
                          checked={subadminForm.onlyPosition}
                          onChange={handleSubadminFormChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="subadmin-onlyPosition"
                        >
                          Only position
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="limitOrders"
                          id="subadmin-limitOrders"
                          checked={subadminForm.limitOrders}
                          onChange={handleSubadminFormChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="subadmin-limitOrders"
                        >
                          Limit orders
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-top">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSubadminProfileOpen(false)}
                    disabled={subadminProfileLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={subadminProfileLoading}
                  >
                    Save changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MasterLayout;
